import testRules from 'lib/testRules';
import advancedMerge from 'lib/advancedMerge';
import evaluateFn from 'lib/evaluateFn';

/**
 * Validates rules by checking that they actually have actions, sets a default priority of 5, and
 * sorts the rules based on their priority
 * @param  {Array<Rule>}  rules List of rules to be processed
 * @return {Array<Rule>}  List of processed rules
 */
export function processRules(rules = []) {
  return rules
    .filter(rule => rule.action)
    .map(rule => (rule.priority ? rule : { ...rule, priority: 5 }))
    .sort((a, b) => a.priority - b.priority);
}

/**
 * A general configuration manager for handling version independent rules and settings
 */
class Configurator {
  _configuration;

  _meta;

  _defaultProps;

  /**
   * @param {Array<Rule>}       configuration Configuration to test against
   * @param {MetaMap|function}  meta          Initially set meta values to test
   * @param {ActionMap}         defaultProps  Default props set before being overridden by
   *   explicit rules
   */
  constructor(
    configuration = [],
    meta = {},
    defaultProps = {},
  ) {
    this.updateConfiguration = this.updateConfiguration.bind(this);
    this.appendConfiguration = this.appendConfiguration.bind(this);
    this.updateMeta = this.updateMeta.bind(this);
    this.getConfiguration = this.getConfiguration.bind(this);
    this.getConfigurationAsync = this.getConfigurationAsync.bind(this);

    this._configuration = processRules(configuration);
    this._meta = meta;
    this._defaultProps = defaultProps;
  }

  /**
   * Updates the configuration in the Configurator instance
   * @param {Array<Rule>} configuration Configuration to test against
   */
  updateConfiguration(configuration) {
    this._configuration = processRules(configuration);
  }

  /**
   * Appends the configuration in the Configurator instance
   * @param {Array<Rule>} configuration Configuration to append to existing rules
   */
  appendConfiguration(configuration) {
    this._configuration = processRules([
      ...this._configuration,
      ...configuration,
    ]);
  }

  /**
   *
   * @param {Object|Function} meta Object or function to replace existing
   * meta with
   */
  updateMeta(meta = {}) {
    this._meta = meta;
  }

  get meta() {
    return evaluateFn(this._meta);
  }

  /**
   * Updates the configuration in the Configurator instance
   * @param {ActionMap} defaults A default set of actions to start from in this context
   * @param {MetaMap|function} meta Additional meta to use against rules
   * @returns {ActionMap} Object of actions that are returned from matching rules
   */
  getConfiguration({ defaults = {}, meta = {} } = {}) {
    let action = { ...this._defaultProps, ...defaults };

    const combinedMetaData = {
      ...evaluateFn(this._meta),
      ...evaluateFn(meta),
    };

    const matchingRules = this._configuration
      .filter((rule) => {
        if (!rule.condition) return true;
        return testRules((!Array.isArray(rule.condition) ? [rule.condition] : rule.condition),
          combinedMetaData, 'and');
      });

    matchingRules.forEach((matchingRule) => {
      action = advancedMerge(action, matchingRule.action);
    });

    return action;
  }

  getConfigurationAsync(...props) {
    // TODO: Make async version
    return Promise.resolve(this.getConfiguration(...props));
  }
}

export default Configurator;
